import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { InterfaceAppServiceRegister, TreeNodeRegister } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { SelectSearchLoad } from '@app/core/libraries/Dynamic-fields/components/select/classes/SelectSearchLoad.class';
import { TreeSelectDialogComponent } from '@app/dialogs-helper/tree-select-dialog/tree-select-dialog.component';
import { FuiDestroyService } from '@services/destroy.service';
import { BehaviorSubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { SelectService } from '../select-service.service';

@Component({
    selector: 'app-input-tree-select',
    templateUrl: './select-tree.component.html',
    styleUrls: ['./../../input/input.scss'],
    providers: [SelectService, FuiDestroyService],
})
/** Разновидность поля select с древовидной структурой */
export class SelectTreeComponent extends SelectSearchLoad implements OnInit, OnChanges {
    /**
     * Тултип для самого поля
     */
    public tooltip = new BehaviorSubject<string | null>(null);

    constructor(
        @Inject(FuiDestroyService) protected _destroy$: FuiDestroyService,
        public appService: AppService,
        public service: SelectService,
        public http: HttpClient,
        public dialog: MatDialog,
    ) {
        super(_destroy$, service, http, appService);
    }

    /** Данные для конфигурации дерева */ // TODO в будущем хотелось бы переименовать
    data: {
        title: string;
        parentId: string | number;
        levelOpen: number;
        listId: null;
        treeData: TreeNodeRegister[];
        isFolderSelect: boolean;
        /** Выбирать только узлы, которые не являются папками */
        isChildrenTree: boolean;
        selectElement: {};
        listViewTreeAsFolder: boolean;
        isHideEmptyFolder: boolean;
        treeIconsObject: {};
    };
    /** Данные дерева по ИД */
    dataTreeId = {};

    /** Ссылка на элемент формы */
    formControl = new FormControl('', []);
    /** Массив предков */
    parentsIds = [];

    /** Логика работы данного поля отличается от родителя, переопределяем ее. */
    ngOnInit(): void {
        if (this.service.field.required) {
            this.formControl.addValidators(Validators.required);
            this.formControl.updateValueAndValidity({ emitEvent: false });
        }

        const control = this.service.group.get(this.service.field.name);

        control.statusChanges.pipe(takeUntil(this._destroy$)).subscribe((status) => {
            this.formControl.enable({ emitEvent: false });
            switch (status) {
                case 'INVALID':
                    this.formControl.setErrors(this.service.group.get(this.service.field.name).errors);
                    if (this.service.group.get(this.service.field.name).touched) {
                        this.formControl.markAsTouched();
                    }
                    break;
                case 'VALID':
                    this.formControl.setErrors([]);
                    break;
                case 'DISABLED':
                    this.formControl.disable();
            }
            this.formControl.updateValueAndValidity({ emitEvent: false });
        });

        this.tooltip.next(this.formControl.value);
        control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
            this.getNameField(value);
            this.tooltip.next(this.formControl.value);
        });

        this.getOptions();
    }

    /** Установка опций */
    getOptions(): void {
        this.service.isShowSpinner.next(true);
        this.service.field.getData.pipe(takeUntil(this._destroy$)).subscribe((list: InterfaceAppServiceRegister[]) => {
            if (list.length > 0) {
                this.service.options = list;
                this.generateTreeData();
                this.getNameField(this.service.group.get(this.service.field.name).value);
            }
            // Событие, которое вызовет изменение опций select.
            if (this.service.field?.getChanges) {
                this.setChanges();
            }
            this.service.isShowSpinner.next(false);
        });
    }

    /** Сохранение данных дерева */
    generateTreeData(): void {
        this.data.treeData = this.service.options;
        // Помещаем наименования по id
        this.data.treeData.forEach((value) => {
            this.dataTreeId[value.id] = value.name;
        });
    }

    /**
     * Получение строки в поле
     */
    getNameField(value) {
        this.parentsIds = [];
        this.getParentNodeFromArray(value, this.data.treeData);
        this.parentsIds.reverse();
        if (this.service.field.multiple) {
            const names = [];
            if (typeof value === 'object' && value?.length) {
                value.forEach((val) => {
                    if (this.dataTreeId[val] && !this.parentsIds.some((item) => item == val)) {
                        names.push(this.dataTreeId[val]);
                    }
                });
                this.formControl.setValue(names.join(', '));
            } else {
                this.formControl.setValue('');
            }
        } else {
            if (this.data.listViewTreeAsFolder && this.parentsIds.length) {
                let strParent = '';
                this.parentsIds.forEach((val) => {
                    if (this.dataTreeId[val]) {
                        strParent += this.dataTreeId[val] + '/';
                    }
                });
                this.formControl.setValue(this.dataTreeId[value] ? strParent + this.dataTreeId[value] : '');
            } else {
                this.formControl.setValue(this.dataTreeId[value] ? this.dataTreeId[value] : '');
            }
        }
        this.formControl.markAsDirty();
    }

    /**
     * Получение родительских элементов
     * @param node - id записи
     * @param data - массив значений полей
     */
    getParentNodeFromArray(node = null, data = null): any {
        if (node && data) {
            data.forEach((key) => {
                if (typeof node == 'object') {
                    if (node.find((item) => item == key.id)) {
                        this.parentsIds.push(key.parent_id);
                        return this.getParentNodeFromArray(key.parent_id, data);
                    }
                } else {
                    if (node == key.id && key.parent_id != key.root_id) {
                        this.parentsIds.push(key.parent_id);
                        return this.getParentNodeFromArray(key.parent_id, data);
                    }
                }
            });
        }
    }

    /** Редактирование поля - открываем окно с деревом */
    editDataInput() {
        // Не позволяем редактировать, пока идет загрузка
        if (this.service.isShowSpinner.value) {
            return;
        }
        const dialogRef = this.dialog.open(TreeSelectDialogComponent, {
            disableClose: true,
            autoFocus: '.input-search-value',
            width: '600px',
            data: {
                title: this.data.title,
                data: {
                    isMultiple: this.service.field.multiple,
                    parentId: this.data.parentId,
                    levelOpen: this.data.levelOpen,
                    treeData: this.data.treeData,
                    isFolderSelect: this.data.isFolderSelect,
                    value: this.service.group?.get(this.service.field.name)?.value,
                    listId: this.data.listId,
                    isHideEmptyFolder: this.data.isHideEmptyFolder,
                    treeIconsObject: {},
                },
            },
        });
        dialogRef.keydownEvents().subscribe((result) => {
            if (result.code === 'Escape') {
                dialogRef.close({ save: false });
            }
        });
        dialogRef.afterClosed().subscribe((result: { save: boolean; data: any }) => {
            if (result.save) {
                this.data.selectElement = result.data;
                if (this.service.field.multiple) {
                    const selected = [];
                    result.data.selected.forEach((node) => {
                        if (!node.is_folder || node.is_folder == '0') {
                            selected.push(node.id);
                        }
                        if (!node.is_children) {
                            selected.push(node.id);
                        }
                    });
                    this.service.group.get(this.service.field.name).setValue(selected);
                } else {
                    this.service.group.get(this.service.field.name).setValue(result.data.id);
                }
                this.getNameField(this.service.group.get(this.service.field.name).value);
                this.service.group.get(this.service.field.name).markAsDirty();
            } else {
                if (
                    this.formControl.touched &&
                    this.formControl.errors &&
                    Object.keys(this.formControl.errors).length
                ) {
                    this.service.group.get(this.service.field.name).setErrors(this.formControl.errors);
                    this.service.group.get(this.service.field.name).markAsTouched({ onlySelf: true });
                }
            }
        });
    }

    /** Изменение опций select */
    setChanges(): void {
        this.service.field.getChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
            this.service.isShowSpinner.next(true);
            value.pipe(take(1), takeUntil(this._destroy$)).subscribe((result) => {
                this.service.options = result;
                this.dataTreeId = {};
                this.generateTreeData();
                this.getNameField(this.service.group.get(this.service.field.name).value);
                this.service.isShowSpinner.next(false);
            });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.field?.currentValue) {
            this.service.field = changes?.field.currentValue;
        }

        if (this.service.field.required) {
            this.formControl.addValidators(Validators.required);

            if (this.service.group.get(this.service.field.name).touched) {
                this.formControl.markAsTouched();
            }
        } else {
            this.formControl.removeValidators(Validators.required);
        }
        this.formControl.updateValueAndValidity({ emitEvent: false });
    }
}
